import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useExpenseCategoriesList, useExpenseCategoryMutations } from "../api";
import {
  buildExpenseCategoryBatchActions,
  type ExpenseCategoryTab,
} from "./batchActions";
import type { ExpenseCategory } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ExpenseCategoryTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useExpenseCategoryMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useExpenseCategoriesList({
      page: { number: pageNumber, size: pageSize },
      filter: { active: tab === "active" },
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<ExpenseCategory>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "rate",
      header: "Amount",
      cell: ({ row }) =>
        row.original.rate == null ? "Variable" : row.original.rate,
    },
    {
      accessorKey: "useResourceRate",
      header: "Use Resource Rate?",
      cell: ({ row }) =>
        row.original.useResourceRate ? <FontAwesomeIcon icon={faCheck} /> : "",
    },
    {
      accessorKey: "phaseName",
      header: "Phase",
      cell: ({ row }) => row.original.phaseName,
    },
    {
      accessorKey: "status",
      header: "Required?",
      cell: ({ row }) =>
        row.original.status === "required" ? (
          <FontAwesomeIcon icon={faCheck} />
        ) : (
          ""
        ),
    },
  ];

  const runOnSelected = async (
    fn: (e: ExpenseCategory) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    let failures = 0;
    for (const e of targets) {
      try {
        await fn(e);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: `Failed on ${failures} of ${targets.length} expense category(ies).`,
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (e) => archive(e.id),
        (n) => `${n} expense category(ies) archived.`
      );
    } else {
      await runOnSelected(
        (e) => restore(e.id, e.name),
        (n) => `${n} expense category(ies) restored.`
      );
    }
  };

  const batchActions = buildExpenseCategoryBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
    }
  );

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Travel &amp; Expense"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add An Expense Category"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as ExpenseCategoryTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No expense categories found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active"
            ? "Archive expense categories?"
            : "Restore expense categories?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} expense category(ies)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
